import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Mesh,
  ShaderMaterial,
  ShapeUtils,
  Vector2,
  Vector3,
  type Texture,
} from "three";
import { DateTime } from "luxon";

import { EARTH_RADIUS } from "./constants";
import { latLngToVec3 } from "./geo";
import { zoneColor } from "./zonecolor";
import { liveOffsets, type ZoneFill } from "./zonefills";

// ============================================================
//  Land mesh — the Time zones map's land as real geometry.
//
//  Each zone's land (zonefills.ts) is triangulated in the lat/lng plane
//  and laid on the sphere just above the globe, so every coast, island
//  and zone boundary is exact at any zoom. Every vertex carries its
//  zone's colour; the colours are rewritten when a live offset (DST) or
//  the theme changes. Shading follows the globe's vector map
//  (earth.frag.glsl): a cool shade on the night side, city lights in
//  the dark theme.
//
//  Large triangles are split until no edge exceeds MAX_EDGE_DEG, so
//  they follow the curve instead of cutting under the surface. The
//  split depends on each edge alone, so neighbouring triangles split a
//  shared edge the same way and no cracks open between them.
// ============================================================

const RADIUS = EARTH_RADIUS * 1.0005;
const MAX_EDGE_DEG = 2;

const VERT = /* glsl */ `
  attribute vec3 aColor;
  varying vec3 vDir;
  varying vec3 vColor;
  void main() {
    vDir = normalize(position);
    vColor = aColor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  uniform sampler2D uNightTex;
  uniform vec3 uSunDir;
  uniform float uTheme;   // 0 = dark, 1 = light
  varying vec3 vDir;
  varying vec3 vColor;
  const float PI = 3.141592653589793;
  void main() {
    vec3 d = normalize(vDir);
    vec3 day = vColor;
    vec3 night = day * mix(vec3(0.34, 0.38, 0.50), vec3(0.86, 0.87, 0.94), uTheme);
    // Faint city lights on the night side, dark theme only.
    vec2 uv = vec2(fract(atan(d.z, -d.x) / (2.0 * PI)), asin(clamp(d.y, -1.0, 1.0)) / PI + 0.5);
    night += pow(texture2D(uNightTex, uv).rgb, vec3(1.2)) * 0.55 * (1.0 - uTheme);
    float cosSun = dot(d, uSunDir);
    gl_FragColor = vec4(mix(night, day, smoothstep(-0.04, 0.06, cosSun)), 1.0);
  }
`;

// Antarctica: plain ice.
const ICE: [number, number, number][] = [
  [77, 87, 102], // dark
  [245, 247, 250], // light
];

export interface LandMesh {
  mesh: Mesh<BufferGeometry, ShaderMaterial>;
  setSunDirection(v: Vector3): void;
  setTheme(t: number): void;
  /** Recompute live offsets; recolours when any changed. */
  update(now: DateTime): void;
}

export function createLandMesh(zones: ZoneFill[], nightTex: Texture, theme: number): LandMesh {
  const lng: number[] = [];
  const lat: number[] = [];
  const zoneOf: number[] = []; // vertex → zone index
  const out: number[] = [];

  const toPoints = (ring: number[]) => {
    const pts: Vector2[] = [];
    for (let i = 0; i < ring.length; i += 2) pts.push(new Vector2(ring[i], ring[i + 1]));
    if (pts.length > 1 && pts[0].equals(pts[pts.length - 1])) pts.pop();
    return pts;
  };

  const edgeLen = (i: number, j: number) => {
    const meanLat = ((lat[i] + lat[j]) / 2) * (Math.PI / 180);
    return Math.hypot(lat[i] - lat[j], (lng[i] - lng[j]) * Math.cos(meanLat));
  };

  zones.forEach((zone, zi) => {
    // Midpoints are shared within a zone, so a zone's triangles meet
    // without cracks; zones meet along the boundary the build cut.
    const midCache = new Map<string, number>();
    const mid = (i: number, j: number) => {
      const key = i < j ? `${i}:${j}` : `${j}:${i}`;
      let m = midCache.get(key);
      if (m === undefined) {
        m = lng.length;
        lng.push((lng[i] + lng[j]) / 2);
        lat.push((lat[i] + lat[j]) / 2);
        zoneOf.push(zi);
        midCache.set(key, m);
      }
      return m;
    };

    for (const poly of zone.polygons) {
      const [outer, ...holes] = poly.map(toPoints);
      if (!outer || outer.length < 3) continue;
      const base = lng.length;
      for (const p of [outer, ...holes].flat()) {
        lng.push(p.x);
        lat.push(p.y);
        zoneOf.push(zi);
      }
      const stack: [number, number, number][] = ShapeUtils.triangulateShape(outer, holes).map(
        ([a, b, c]) => [base + a, base + b, base + c],
      );
      while (stack.length) {
        const [a, b, c] = stack.pop()!;
        const sAB = edgeLen(a, b) > MAX_EDGE_DEG;
        const sBC = edgeLen(b, c) > MAX_EDGE_DEG;
        const sCA = edgeLen(c, a) > MAX_EDGE_DEG;
        const n = +sAB + +sBC + +sCA;
        if (n === 0) {
          out.push(a, b, c);
        } else if (n === 3) {
          const ab = mid(a, b), bc = mid(b, c), ca = mid(c, a);
          stack.push([a, ab, ca], [ab, b, bc], [ca, bc, c], [ab, bc, ca]);
        } else if (n === 1) {
          // Rotate so the split edge is a–b.
          const [p, q, r] = sAB ? [a, b, c] : sBC ? [b, c, a] : [c, a, b];
          const m = mid(p, q);
          stack.push([p, m, r], [m, q, r]);
        } else {
          // Two edges split: rotate so they are a–b and b–c.
          const [p, q, r] = !sCA ? [a, b, c] : !sAB ? [b, c, a] : [c, a, b];
          const m1 = mid(p, q), m2 = mid(q, r);
          stack.push([m1, q, m2], [p, m1, m2], [p, m2, r]);
        }
      }
    }
  });

  const positions = new Float32Array(lng.length * 3);
  const v = new Vector3();
  for (let i = 0; i < lng.length; i++) {
    latLngToVec3(lat[i], lng[i], RADIUS, v);
    positions.set([v.x, v.y, v.z], i * 3);
  }
  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  // Recoloured in place: the attribute keeps its own copy of the array.
  const colorAttr = new Float32BufferAttribute(new Float32Array(lng.length * 3), 3);
  const colors = colorAttr.array as Float32Array;
  geo.setAttribute("aColor", colorAttr);
  geo.setIndex(out);

  const material = new ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uNightTex: { value: nightTex },
      uSunDir: { value: new Vector3(1, 0, 0) },
      uTheme: { value: theme },
    },
    // Triangle winding follows each ring's direction; the globe hides
    // the far side, so both faces are drawn.
    side: DoubleSide,
  });
  const mesh = new Mesh(geo, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  mesh.visible = false;

  let currentTheme = theme;
  let offsets: (number | null)[] = [];
  function recolour(): void {
    // Colours are display values: the shader writes them straight out.
    const perZone = offsets.map((o) =>
      (o === null ? ICE[currentTheme > 0.5 ? 1 : 0] : zoneColor(o, currentTheme)).map((c) => c / 255),
    );
    for (let i = 0; i < zoneOf.length; i++) colors.set(perZone[zoneOf[i]], i * 3);
    colorAttr.needsUpdate = true;
  }
  offsets = liveOffsets(zones, DateTime.utc());
  recolour();

  let lastMinute = -1;
  return {
    mesh,
    setSunDirection(dir) {
      material.uniforms.uSunDir.value.copy(dir).normalize();
    },
    setTheme(t) {
      material.uniforms.uTheme.value = t;
      if (t === currentTheme) return;
      currentTheme = t;
      recolour();
    },
    update(now) {
      const minute = Math.floor(now.toMillis() / 60000);
      if (minute === lastMinute) return;
      lastMinute = minute;
      const next = liveOffsets(zones, now);
      if (next.some((o, i) => o !== offsets[i])) {
        offsets = next;
        recolour();
      }
    },
  };
}
